import * as React from 'react';
import { clsx } from 'clsx';

export interface ButtonSegmentedItem {
  /**
   * The label text for the button
   */
  label: string;
  /**
   * Optional icon to display before the label
   */
  icon?: React.ReactNode;
  /**
   * Optional value identifier for the button
   */
  value?: string;
}

export interface ButtonSegmentedProps {
  /**
   * Array of button items (minimum 2, maximum 4)
   */
  items: ButtonSegmentedItem[];
  /**
   * The index of the currently active button
   * @default 0
   */
  activeIndex?: number;
  /**
   * Callback when a button is clicked
   */
  onChange?: (index: number, item: ButtonSegmentedItem) => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Arbor Design System segmented button styles
const segmentedStyles = {
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #efefef',
    borderRadius: '99px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  button: {
    base: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '99px',
      border: 'none',
      cursor: 'pointer',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '13px',
      fontWeight: '600',
      lineHeight: '1.5',
      transition: 'all 0.15s ease-in-out',
      outline: 'none',
      whiteSpace: 'nowrap' as const,
    },
    default: {
      backgroundColor: 'transparent',
      color: '#595959',
    },
    hover: {
      backgroundColor: '#f8f8f8',
      color: '#2f2f2f',
    },
    active: {
      backgroundColor: '#0e8a0e',
      color: '#ffffff',
    },
    activeHover: {
      backgroundColor: '#005700',
      color: '#ffffff',
    },
  },
};

/**
 * ButtonSegmented component - Arbor Design System
 *
 * A segmented button group where one button is always active.
 * Supports 2-4 buttons with optional icons.
 *
 * @example
 * ```tsx
 * <ButtonSegmented
 *   items={[
 *     { label: 'Day' },
 *     { label: 'Week' },
 *     { label: 'Month' },
 *   ]}
 *   activeIndex={0}
 *   onChange={(index, item) => console.log(index, item)}
 * />
 * ```
 */
export const ButtonSegmented = React.forwardRef<HTMLDivElement, ButtonSegmentedProps>(
  ({ items, activeIndex = 0, onChange, className, style, ...props }, ref) => {
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

    // Validate items count (2-4)
    const validItems = items.slice(0, 4);
    if (validItems.length < 2) {
      console.warn('ButtonSegmented requires at least 2 items');
    }

    // Check if any item has an icon - if so, all should have icons
    const hasIcons = validItems.some((item) => item.icon);

    const handleClick = (index: number) => {
      if (index !== activeIndex && onChange) {
        onChange(index, validItems[index]);
      }
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-button-segmented', className)}
        style={{ ...segmentedStyles.container, ...style }}
        role="group"
        aria-label="Segmented button group"
        {...props}
      >
        {validItems.map((item, index) => {
          const isActive = index === activeIndex;
          const isHovered = hoveredIndex === index;

          let buttonStyle: React.CSSProperties = {
            ...segmentedStyles.button.base,
          };

          if (isActive) {
            buttonStyle = {
              ...buttonStyle,
              ...segmentedStyles.button.active,
              ...(isHovered && segmentedStyles.button.activeHover),
            };
          } else {
            buttonStyle = {
              ...buttonStyle,
              ...segmentedStyles.button.default,
              ...(isHovered && segmentedStyles.button.hover),
            };
          }

          return (
            <button
              key={item.value || index}
              type="button"
              style={buttonStyle}
              onClick={() => handleClick(index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              aria-pressed={isActive}
            >
              {hasIcons && item.icon && (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '16px',
                    height: '16px',
                  }}
                >
                  {item.icon}
                </span>
              )}
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }
);

ButtonSegmented.displayName = 'ButtonSegmented';
